import type { PrescriptionDocumentData, PrescriptionItemData } from "../prescription/renderPrescriptionBody";
import type { HeaderTokenValues } from "../layout/headerTokens";

export type SamplePrescriptionVariant = "simples" | "controle" | "manipulada" | "exame";
type Lang = "pt-BR" | "en";

export const SAMPLE_HEADER_TOKENS: HeaderTokenValues = {
  clinic_name: "Clínica Veterinária Bem-Estar", clinic_cnpj: "00.000.000/0001-00",
  vet_name: "Dra. Marina Costa Ribeiro", vet_crmv: "CRMV-PR 12345", vet_mapa: "00000/2021",
  vet_phone: "(41) 3333-0000", vet_address: "Rua das Acácias, 120 — Centro, Curitiba/PR",
};

const freeItem = (medicationName: string, quantity: string, posology: string): PrescriptionItemData => ({
  medicationName, concentration: null, presentation: null, quantity,
  pharmaceuticalForm: null, administrationRoute: "Oral", posology_mode: "free_text", free_text_posology: posology,
});

const base: PrescriptionDocumentData = {
  type: "standard", issuedAt: "2026-10-02", validityInDays: 30,
  observations: "Oferecer água à vontade. Em caso de vômito, suspender e entrar em contato.",
  vetName: "Dra. Marina Costa Ribeiro", crmv: "CRMV-PR 12345", vetPhone: "4133330000",
  vetAddress: "Rua das Acácias, 120 — Centro, Curitiba/PR", clinicName: "Clínica Veterinária Bem-Estar",
  controlRegime: "none", mapaRegistration: "00000/2021", buyer: null,
  pet: { name: "Thor", ownerName: "Carlos Eduardo Lima", ownerDocument: "123.456.789-00",
    ownerAddress: "Rua Itupava, 845, ap. 32 – Alto da XV, Curitiba/PR, CEP 80045-000",
    species: "Canina", breed: "Labrador Retriever", gender: "Macho castrado", birthDate: "2019-03-12", weightKg: 32.4 },
  items: [
    freeItem("Meloxicam 2 mg — comprimido", "1 caixa (10 comp.)", "Administrar 1 comprimido por via oral, a cada 24 horas, por 5 dias, após a refeição."),
    freeItem("Omeprazol 20 mg — cápsula", "1 caixa (14 cáps.)", "Administrar 1 cápsula por via oral, em jejum, a cada 24 horas, por 7 dias."),
  ],
  examItems: [],
};

export function buildSamplePrescriptionData(variant: SamplePrescriptionVariant): PrescriptionDocumentData {
  if (variant === "controle") return {
    ...base, type: "controlled", controlRegime: "controlled_human",
    observations: "Retorno em 30 dias. Manter fora do alcance de crianças e outros animais.",
    items: [
      freeItem("Fenobarbital 100 mg — comprimido", "1 caixa (30 comp.)", "Administrar 1/2 comprimido por via oral, a cada 12 horas, uso contínuo. Não interromper sem orientação."),
      freeItem("Trazodona 50 mg — comprimido", "150 comprimidos", "Administrar 2,5 comprimidos por via oral, a cada 12 horas, por 30 dias."),
    ],
  };
  if (variant === "manipulada") return {
    ...base, type: "manipulated", items: [], observations: "Conservar em geladeira.",
    manipulatedPayload: {
      components: [
        { name: "Gabapentina", concentration: "10 mg/kg" }, { name: "Amitriptilina", concentration: "1 mg/kg" },
        { name: "Pasta oral palatável sabor frango", concentration: "q.s.p. 1 dose" },
      ],
      treatmentDays: "30", totalDoses: "60", route: "Oral", pharmaceuticalForm: "Pasta oral",
      instructions: "Administrar 1 dose por via oral a cada 12 horas, junto com o alimento.", freeText: "",
    },
  };
  if (variant === "exame") return {
    ...base, type: "exam", items: [], observations: "Levar este pedido no dia da coleta.",
    examItems: [
      { examName: "Hemograma completo", instructions: "Jejum alimentar de 8 horas." },
      { examName: "Perfil bioquímico renal (ureia, creatinina, SDMA)", instructions: null },
      { examName: "Urinálise (urina tipo I)", instructions: "Coleta por cistocentese." },
    ],
  };
  return base;
}

export const SAMPLE_TERM_TITLE: Record<Lang, string> = { "pt-BR": "Termo de consentimento para procedimento", en: "Procedure consent form" };
export const SAMPLE_TERM_HTML: Record<Lang, string> = {
  "pt-BR": "<h1>Termo de consentimento</h1><p>Eu, <b>Carlos Eduardo Lima</b>, responsável pelo animal <b>Thor</b>, autorizo a realização do procedimento descrito, ciente dos riscos explicados pela equipe veterinária.</p><ul><li>Jejum alimentar de 8 horas.</li><li>Retorno em 10 dias.</li></ul>",
  en: "<h1>Consent form</h1><p>I, <b>Carlos Eduardo Lima</b>, owner of <b>Thor</b>, authorize the described procedure, aware of the risks explained by the veterinary team.</p><ul><li>8-hour fasting.</li><li>Follow-up in 10 days.</li></ul>",
};
